import { Box } from "@mui/material";
import { CloseIcon, IconButton, PlusIcon } from "@flat-ui";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  setFilterMenuAnchorPosition,
  setIsFilterMenuOpen,
  setOriginalColumnPreferences,
} from "@store/table/table-slice";
import { useRef } from "react";
import { SettingsApiTypes } from "@stellar/settings-api-client";

/**
 * Props for FaroTableColumnVisibilityMenuIcon.
 *
 * @property columnPreferences - The current map of column preferences,
 * including visibility, order, and sort state for each column. Used to determine which columns
 * are visible and to snapshot preferences when opening the menu.
 */
interface Props {
  /** Complete column preferences including visibility, order, and sort state */
  columnPreferences: SettingsApiTypes.ColumnPreferenceMap | undefined;
}

/**
 * Fixed width (in pixels) of the column visibility menu.
 * Used to calculate the anchor position of the menu relative to the trigger button.
 */
const MENU_WIDTH = 210;

/**
 * Renders the icon button that triggers the column visibility menu for the table.
 * Displays a plus icon when the menu is closed and a close icon when the menu is open.
 * The button is right-aligned and dispatches an action to open the menu on click.
 */
export function FaroTableColumnVisibilityMenuIcon({
  columnPreferences,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { isOpen } = useAppSelector((state) => state.table.filterMenu);

  const buttonRef = useRef<HTMLButtonElement>(null);

  function updateAnchorPosition(): void {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const top = rect.bottom + window.scrollY;
      const left = rect.left + window.scrollX - MENU_WIDTH;
      dispatch(setFilterMenuAnchorPosition({ top, left }));
    }
  }

  function handleOpenMenu(): void {
    dispatch(setIsFilterMenuOpen(true));
    dispatch(setOriginalColumnPreferences(columnPreferences));
    updateAnchorPosition();
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
      <IconButton
        size="s"
        margin={0.5}
        onClick={handleOpenMenu}
        ref={buttonRef}
      >
        {isOpen ? <CloseIcon /> : <PlusIcon />}
      </IconButton>
    </Box>
  );
}
